import type { Candle, CandleInterval } from '../types';
import { netPnlPercentFromEntryExit } from '../strategies/common/fees';

interface MurreyLevels {
  eightEight: number;
  fourEight: number;
  zeroEight: number;
  increment: number;
  absTop: number;
}

interface StrategySettings {
  timeframe: CandleInterval;
  supportMode: 'support75' | 'support100' | 'support75Or100';
  rangeScale: number;
  useOneMinuteStopRefinement: boolean;
  requireMa200RisingForEntries: boolean;
  entryBodyPctMin: number;
  entryClosePosMin: number;
  entryDistAboveMa200PctMin: number;
  makerFeePercent: number;
  takerFeePercent: number;
  stopLossPercent: number;
  breakEvenTriggerR: number;
  breakEvenOffsetPercent: number;
  atrPeriod: number;
  atrMultiplier: number;
  queueExpiryHours: number;
  trailingMode: 'legacy' | 'smart';
  smartTrailArmR: number;
  smartTrailLockR: number;
  smartTrailMaxGivebackR: number;
  smartTrailAtrMultiplier: number;
  smartTrailTightenR: number;
  smartTrailTightenAtrMultiplier: number;
}

export type StrategyState = 'idle' | 'pendingBuy' | 'inTrade' | 'closed';

export interface StrategyTrade {
  entryIndex: number;
  entryPrice: number;
  limitPrice: number;
  entryTime: string | null;
  initialStopPrice: number;
  finalStopPrice: number;
  highestHigh: number;
  exitIndex: number | null;
  exitPrice: number | null;
  exitTime: string | null;
  durationCandles: number | null;
  executionResolution: '1m-refined' | 'timeframe-fallback';
  exitReason: 'trailingStop' | 'stopLoss' | null;
  pnlPercent: number | null;
  lowestLow?: number;
  mfeR?: number | null;
  maeR?: number | null;
  realizedR?: number | null;
}

export interface StrategyMarker {
  index: number;
  type: 'buy' | 'sell';
  reason?: 'trailingStop' | 'stopLoss';
}

export interface StrategyStats {
  totalTrades: number;
  trailingStopCount: number;
  stopLossCount: number;
  tradesPerDay: number;
  winRate: number;
  totalPnlPercent: number;
  averagePnlPercent: number;
}

export interface StrategyActiveTrade {
  limitPrice: number;
  stopLossPrice: number;
  state: StrategyState;
  entryIndex: number | null;
  entryPrice: number | null;
}

export interface StrategyOverlayResult {
  markers: StrategyMarker[];
  trades: StrategyTrade[];
  stats: StrategyStats;
  activeTrade: StrategyActiveTrade | null;
}

const LOOKBACK_128 = 128;
const LOOKBACK_32 = 32;
const LOOKBACK_16 = 16;
const LOOKBACK_8 = 8;

const ma200SeriesCache = new WeakMap<Candle[], (number | undefined)[]>();
const supportSignalSeriesCache = new WeakMap<Candle[], Map<string, boolean[]>>();
const minuteChildMapCache = new WeakMap<Candle[], Map<number, Map<number, Candle[]>>>();

function timeframeToMilliseconds(interval: CandleInterval): number {
  if (interval === '1m') {
    return 60_000;
  }
  if (interval === '3m') {
    return 3 * 60_000;
  }
  if (interval === '5m') {
    return 5 * 60_000;
  }
  if (interval === '15m') {
    return 15 * 60_000;
  }
  if (interval === '30m') {
    return 30 * 60_000;
  }
  if (interval === '45m') {
    return 45 * 60_000;
  }
  if (interval === '1h') {
    return 60 * 60_000;
  }
  if (interval === '2h') {
    return 2 * 60 * 60_000;
  }
  if (interval === '3h') {
    return 3 * 60 * 60_000;
  }
  if (interval === '4h') {
    return 4 * 60 * 60_000;
  }
  if (interval === '1d') {
    return 24 * 60 * 60_000;
  }
  if (interval === '1w') {
    return 7 * 24 * 60 * 60_000;
  }
  return 30 * 24 * 60 * 60_000;
}

function toNewestFirst(candles: Candle[]): Candle[] {
  return [...candles].reverse();
}

function lowest(values: number[], lookback: number): number {
  let low = Number.POSITIVE_INFINITY;
  for (let index = 0; index <= lookback && index < values.length; index += 1) {
    if (values[index] < low) {
      low = values[index];
    }
  }
  return low;
}

function highest(values: number[], lookback: number): number {
  let high = Number.NEGATIVE_INFINITY;
  for (let index = 0; index <= lookback && index < values.length; index += 1) {
    if (values[index] > high) {
      high = values[index];
    }
  }
  return high;
}

function murreyLookback(highs: number[], lows: number[], lookback: number): MurreyLevels {
  const logTen = Math.log(10);
  const log8 = Math.log(8);
  const log2 = Math.log(2);

  const vLow = lowest(lows, lookback);
  const vHigh = highest(highs, lookback);
  const vDist = vHigh - vLow;
  const tmpHigh = vLow < 0 ? 0 - vLow : vHigh;
  const tmpLow = vLow < 0 ? 0 - vLow - vDist : vLow;
  const shifted = vLow < 0;

  const safeTmpHigh = Math.max(tmpHigh, 1e-9);
  const sfVarRaw = Math.log(0.4 * safeTmpHigh) / logTen;
  const sfVar = sfVarRaw - Math.floor(sfVarRaw);

  const sr =
    safeTmpHigh > 25
      ? sfVar > 0
        ? Math.exp(logTen * (Math.floor(Math.log(0.4 * safeTmpHigh) / logTen) + 1))
        : Math.exp(logTen * Math.floor(Math.log(0.4 * safeTmpHigh) / logTen))
      : 100 * Math.exp(log8 * Math.floor(Math.log(0.005 * safeTmpHigh) / log8));

  const range = Math.max(safeTmpHigh - tmpLow, 1e-9);
  const nVar1 = Math.log(sr / range) / log8;
  const nVar2 = nVar1 - Math.floor(nVar1);
  const n = nVar1 <= 0 ? 0 : nVar2 === 0 ? Math.floor(nVar1) : Math.floor(nVar1) + 1;

  const si = sr * Math.exp(-n * log8);
  const m = Math.floor((1 / log2) * Math.log(range / si) + 0.0000001);
  const i = Math.round(((safeTmpHigh + tmpLow) * 0.5) / (si * Math.exp((m - 1) * log2)));

  const bot = (i - 1) * si * Math.exp((m - 1) * log2);
  const top = (i + 1) * si * Math.exp((m - 1) * log2);

  const doShift = safeTmpHigh - top > 0.25 * (top - bot) || bot - tmpLow > 0.25 * (top - bot);
  const mm = !doShift ? m : doShift && m < 2 ? m + 1 : 0;
  const nn = !doShift ? n : doShift && m < 2 ? n : n - 1;

  const finalSi = doShift ? sr * Math.exp(-nn * log8) : si;
  const finalI = doShift
    ? Math.round(((safeTmpHigh + tmpLow) * 0.5) / (finalSi * Math.exp((mm - 1) * log2)))
    : i;
  const finalBot = doShift ? (finalI - 1) * finalSi * Math.exp((mm - 1) * log2) : bot;
  const finalTop = doShift ? (finalI + 1) * finalSi * Math.exp((mm - 1) * log2) : top;

  const increment = (finalTop - finalBot) / 8;
  const absTop = shifted ? -(finalBot - 3 * increment) : finalTop + 3 * increment;

  const eightEight = absTop - 3 * increment;
  const fourEight = absTop - 7 * increment;
  const zeroEight = absTop - 11 * increment;

  return {
    eightEight,
    fourEight,
    zeroEight,
    increment,
    absTop,
  };
}

function levelsArray(levels: MurreyLevels): number[] {
  return [levels.eightEight, levels.fourEight, levels.zeroEight];
}

function withinTick(a: number, b: number, tick: number): boolean {
  return Math.abs(a - b) <= tick;
}

function anyEqual(values: number[], target: number, tick: number): boolean {
  for (const value of values) {
    if (withinTick(value, target, tick)) {
      return true;
    }
  }
  return false;
}

function inferTickSize(symbol: string, candles: Candle[]): number {
  if (symbol.toUpperCase() === 'XBTUSD') {
    return 0.5;
  }

  let maxPrecision = 0;
  for (let index = 0; index < candles.length && index < 120; index += 1) {
    const candle = candles[index];
    const values = [candle.open, candle.high, candle.low, candle.close];
    for (const value of values) {
      const text = value.toString();
      const decimalIndex = text.indexOf('.');
      if (decimalIndex < 0) {
        continue;
      }
      const precision = text.length - decimalIndex - 1;
      if (precision > maxPrecision) {
        maxPrecision = precision;
      }
    }
  }

  if (maxPrecision <= 0) {
    return 1;
  }

  return 1 / 10 ** maxPrecision;
}

function computeSupportSignal(
  candles: Candle[],
  symbol: string,
  candleIndex: number,
  supportMode: StrategySettings['supportMode'],
): boolean {
  if (candleIndex < LOOKBACK_128) {
    return false;
  }

  const slice = candles.slice(0, candleIndex + 1);
  const newest = toNewestFirst(slice);
  const highs = newest.map((c) => c.high);
  const lows = newest.map((c) => c.low);

  const levels128 = murreyLookback(highs, lows, LOOKBACK_128);
  const levels32 = murreyLookback(highs, lows, LOOKBACK_32);
  const levels16 = murreyLookback(highs, lows, LOOKBACK_16);
  const levels8 = murreyLookback(highs, lows, LOOKBACK_8);

  const tick = inferTickSize(symbol, newest);

  const l0 = levelsArray(levels128);
  const l1 = levelsArray(levels32);
  const l2 = levelsArray(levels16);
  const l3 = levelsArray(levels8);

  const support75a =
    anyEqual(l1, levels128.zeroEight, tick) &&
    anyEqual(l2, levels128.zeroEight, tick) &&
    !withinTick(levels8.zeroEight, levels128.zeroEight, tick) &&
    !withinTick(levels8.fourEight, levels128.zeroEight, tick);

  const support75b =
    anyEqual(l2, levels32.zeroEight, tick) &&
    anyEqual(l3, levels32.zeroEight, tick) &&
    !withinTick(levels128.zeroEight, levels32.zeroEight, tick) &&
    !withinTick(levels128.fourEight, levels32.zeroEight, tick);

  const support100 =
    anyEqual(l1, levels128.zeroEight, tick) &&
    anyEqual(l2, levels128.zeroEight, tick) &&
    anyEqual(l3, levels128.zeroEight, tick);

  if (supportMode === 'support100') {
    return support100;
  }

  if (supportMode === 'support75') {
    return support75a || support75b;
  }

  return support75a || support75b || support100;
}

function resolveLongStopExitPrice(stopPrice: number, candleOpen: number): number {
  if (Number.isFinite(candleOpen) && candleOpen < stopPrice) {
    return candleOpen;
  }
  return stopPrice;
}

function computeAtr(candles: Candle[], index: number, period: number): number | null {
  const safePeriod = Math.max(1, Math.floor(period));
  if (index <= 0 || index >= candles.length) {
    return null;
  }

  const start = Math.max(1, index - safePeriod + 1);
  let sum = 0;
  let count = 0;

  for (let cursor = start; cursor <= index; cursor += 1) {
    const candle = candles[cursor];
    const previousClose = candles[cursor - 1].close;
    const highLow = candle.high - candle.low;
    const highClose = Math.abs(candle.high - previousClose);
    const lowClose = Math.abs(candle.low - previousClose);
    const trueRange = Math.max(highLow, highClose, lowClose);
    sum += trueRange;
    count += 1;
  }

  if (count === 0) {
    return null;
  }

  return sum / count;
}

function computeSimpleMovingAverageSeries(candles: Candle[], period: number): (number | undefined)[] {
  const normalizedPeriod = Math.max(1, Math.floor(period));
  const values: (number | undefined)[] = new Array(candles.length).fill(undefined);
  let rollingSum = 0;

  for (let index = 0; index < candles.length; index += 1) {
    rollingSum += candles[index].close;
    if (index >= normalizedPeriod) {
      rollingSum -= candles[index - normalizedPeriod].close;
    }

    if (index >= normalizedPeriod - 1) {
      values[index] = rollingSum / normalizedPeriod;
    }
  }

  return values;
}

function getCachedSimpleMovingAverageSeries(candles: Candle[], period: number): (number | undefined)[] {
  if (period !== 200) {
    return computeSimpleMovingAverageSeries(candles, period);
  }
  const cached = ma200SeriesCache.get(candles);
  if (cached) {
    return cached;
  }
  const computed = computeSimpleMovingAverageSeries(candles, period);
  ma200SeriesCache.set(candles, computed);
  return computed;
}

function getCachedSupportSignalSeries(
  candles: Candle[],
  symbol: string,
  supportMode: StrategySettings['supportMode'],
): boolean[] {
  let perCandleCache = supportSignalSeriesCache.get(candles);
  if (!perCandleCache) {
    perCandleCache = new Map<string, boolean[]>();
    supportSignalSeriesCache.set(candles, perCandleCache);
  }

  const key = `${symbol.toUpperCase()}::${supportMode}`;
  const cached = perCandleCache.get(key);
  if (cached) {
    return cached;
  }

  const computed = new Array<boolean>(candles.length).fill(false);
  for (let index = LOOKBACK_128; index < candles.length; index += 1) {
    computed[index] = computeSupportSignal(candles, symbol, index, supportMode);
  }
  perCandleCache.set(key, computed);
  return computed;
}

function toCandleTimeMs(candle: Candle): number {
  return Date.parse(candle.time);
}

function bucketStartForInterval(timeMs: number, intervalMs: number): number {
  return Math.floor(timeMs / intervalMs) * intervalMs;
}

function buildMinuteChildMap(minuteCandles: Candle[], timeframeMs: number): Map<number, Candle[]> {
  const grouped = new Map<number, Candle[]>();

  for (const candle of minuteCandles) {
    const timeMs = toCandleTimeMs(candle);
    if (!Number.isFinite(timeMs)) {
      continue;
    }

    const bucketStart = bucketStartForInterval(timeMs, timeframeMs);
    const current = grouped.get(bucketStart);
    if (current) {
      current.push(candle);
    } else {
      grouped.set(bucketStart, [candle]);
    }
  }

  for (const [, bucket] of grouped) {
    bucket.sort((left, right) => toCandleTimeMs(left) - toCandleTimeMs(right));
  }

  return grouped;
}

function getCachedMinuteChildMap(minuteCandles: Candle[], timeframeMs: number): Map<number, Candle[]> {
  let perSeriesCache = minuteChildMapCache.get(minuteCandles);
  if (!perSeriesCache) {
    perSeriesCache = new Map<number, Map<number, Candle[]>>();
    minuteChildMapCache.set(minuteCandles, perSeriesCache);
  }
  const cached = perSeriesCache.get(timeframeMs);
  if (cached) {
    return cached;
  }
  const computed = buildMinuteChildMap(minuteCandles, timeframeMs);
  perSeriesCache.set(timeframeMs, computed);
  return computed;
}

function applyHistoryWindow(
  candles: Candle[],
  maxHistoryCandles: number | undefined,
): { candles: Candle[]; indexOffset: number } {
  if (maxHistoryCandles === undefined || !Number.isFinite(maxHistoryCandles)) {
    return { candles, indexOffset: 0 };
  }

  const cappedLength = Math.floor(maxHistoryCandles);
  if (cappedLength <= 0 || candles.length <= cappedLength) {
    return { candles, indexOffset: 0 };
  }

  const indexOffset = candles.length - cappedLength;
  return {
    candles: candles.slice(indexOffset),
    indexOffset,
  };
}

function computeRMultiple(entryPrice: number, referencePrice: number, initialStopPrice: number): number | null {
  const riskPerUnit = entryPrice - initialStopPrice;
  if (!Number.isFinite(riskPerUnit) || riskPerUnit <= 0) {
    return null;
  }
  return (referencePrice - entryPrice) / riskPerUnit;
}

export function computeTraderPro17StrategyOverlay(
  candles: Candle[],
  _overlay: unknown,
  settings: StrategySettings,
  symbol = 'XBTUSD',
  maxHistoryCandles?: number,
  minuteCandles?: Candle[],
): StrategyOverlayResult {
  const markers: StrategyMarker[] = [];
  const trades: StrategyTrade[] = [];
  const stats: StrategyStats = {
    totalTrades: 0,
    trailingStopCount: 0,
    stopLossCount: 0,
    tradesPerDay: 0,
    winRate: 0,
    totalPnlPercent: 0,
    averagePnlPercent: 0,
  };

  if (!candles.length) {
    return { markers, trades, stats, activeTrade: null };
  }

  const { candles: strategyCandles, indexOffset } = applyHistoryWindow(candles, maxHistoryCandles);
  const timeframeMs = timeframeToMilliseconds(settings.timeframe);
  const requiredMinuteChildren = Math.max(1, Math.round(timeframeMs / 60_000));
  const minuteChildrenByStrategyBucketStart =
    settings.useOneMinuteStopRefinement &&
    Array.isArray(minuteCandles) &&
    minuteCandles.length > 0
      ? getCachedMinuteChildMap(minuteCandles, timeframeMs)
      : null;

  type TradeState = 'idle' | 'pendingBuy' | 'inTrade';
  let state: TradeState = 'idle';
  let pendingLimitPrice: number | null = null;
  let pendingSlPrice: number | null = null;
  let pendingQueuedAtMs: number | null = null;
  let entryIndex: number | null = null;
  let entryPrice: number | null = null;
  let initialStopPrice: number | null = null;
  let activeStopPrice: number | null = null;
  let highestHighSinceEntry: number | null = null;
  let lowestLowSinceEntry: number | null = null;
  let profitableTrades = 0;
  const queueExpiryMs = Math.max(0, settings.queueExpiryHours) * 60 * 60 * 1000;
  const effectiveQueueExpiryMs = Math.max(queueExpiryMs, timeframeToMilliseconds(settings.timeframe));
  const ma200Series = getCachedSimpleMovingAverageSeries(strategyCandles, 200);
  const supportSignals = getCachedSupportSignalSeries(strategyCandles, symbol, settings.supportMode);
  for (let index = 0; index < strategyCandles.length; index += 1) {
    const candle = strategyCandles[index];
    const { low, high, close } = candle;

    if (state === 'idle') {
      const hasSignal = supportSignals[index] ?? false;
      const candleRange = high - low;
      const entryBodyPct = candleRange > 0 ? (close - candle.open) / candleRange : 0;
      const entryClosePos = candleRange > 0 ? (close - low) / candleRange : 0.5;
      const ma200Value = ma200Series[index] ?? null;
      const entryDistAboveMa200Pct =
        ma200Value !== null && ma200Value !== 0 ? ((close - ma200Value) / ma200Value) * 100 : 0;
      const ma200RisingGatePassed =
        !settings.requireMa200RisingForEntries ||
        (ma200Series[index] !== undefined &&
          ma200Series[index - 1] !== undefined &&
          ma200Series[index]! > ma200Series[index - 1]!);
      const entryQualityGatePassed =
        entryBodyPct >= settings.entryBodyPctMin &&
        entryClosePos >= settings.entryClosePosMin &&
        entryDistAboveMa200Pct >= settings.entryDistAboveMa200PctMin;
      if (hasSignal && ma200RisingGatePassed && entryQualityGatePassed) {
        const limitPrice = close;
        const slMultiplier = 1 - settings.stopLossPercent / 100;

        state = 'pendingBuy';
        pendingLimitPrice = limitPrice;
        pendingSlPrice = limitPrice * slMultiplier;
        pendingQueuedAtMs = toCandleTimeMs(candle);
      }
    } else if (state === 'pendingBuy') {
      const currentCandleTimeMs = toCandleTimeMs(candle);
      const pendingExpired =
        pendingQueuedAtMs !== null &&
        Number.isFinite(pendingQueuedAtMs) &&
        Number.isFinite(currentCandleTimeMs) &&
        currentCandleTimeMs - pendingQueuedAtMs > effectiveQueueExpiryMs;

      if (pendingExpired) {
        state = 'idle';
        pendingLimitPrice = null;
        pendingSlPrice = null;
        pendingQueuedAtMs = null;
        continue;
      }

      if (
        pendingLimitPrice !== null &&
        pendingSlPrice !== null &&
        low <= pendingLimitPrice &&
        high >= pendingLimitPrice
      ) {
        state = 'inTrade';
        entryIndex = index;
        entryPrice = pendingLimitPrice;
        initialStopPrice = pendingSlPrice;
        activeStopPrice = pendingSlPrice;
        highestHighSinceEntry = Math.max(high, pendingLimitPrice);
        lowestLowSinceEntry = Math.min(low, pendingLimitPrice);
        pendingQueuedAtMs = null;
        markers.push({ index, type: 'buy' });
      }
    } else if (state === 'inTrade') {
      if (
        activeStopPrice === null ||
        initialStopPrice === null ||
        entryPrice === null ||
        entryIndex === null ||
        highestHighSinceEntry === null ||
        lowestLowSinceEntry === null
      ) {
        state = 'idle';
        pendingLimitPrice = null;
        pendingSlPrice = null;
        pendingQueuedAtMs = null;
        entryIndex = null;
        entryPrice = null;
        initialStopPrice = null;
        activeStopPrice = null;
        highestHighSinceEntry = null;
        lowestLowSinceEntry = null;
        continue;
      }

      const updateTrailingStop = (candidateHigh: number, candidateLow: number) => {
        highestHighSinceEntry = Math.max(highestHighSinceEntry!, candidateHigh);
        lowestLowSinceEntry = Math.min(lowestLowSinceEntry!, candidateLow);

        const riskPerUnit = entryPrice! - initialStopPrice!;
        if (!Number.isFinite(riskPerUnit) || riskPerUnit <= 0) {
          return;
        }

        const breakEvenTriggerPrice = entryPrice! + riskPerUnit * settings.breakEvenTriggerR;
        const atr = computeAtr(strategyCandles, index, settings.atrPeriod);

        let nextStop: number = activeStopPrice!;

        if (settings.trailingMode === 'smart') {
          const currentR = (highestHighSinceEntry - entryPrice!) / riskPerUnit;
          if (highestHighSinceEntry >= breakEvenTriggerPrice) {
            const breakEvenLockPrice = entryPrice! + riskPerUnit * settings.smartTrailLockR;
            nextStop = Math.max(nextStop, breakEvenLockPrice);
          }

          if (currentR >= settings.smartTrailArmR) {
            const dynamicAtrMultiplier =
              currentR >= settings.smartTrailTightenR
                ? settings.smartTrailTightenAtrMultiplier
                : settings.smartTrailAtrMultiplier;

            const atrTrailStop =
              atr !== null && Number.isFinite(atr)
                ? highestHighSinceEntry - atr * dynamicAtrMultiplier
                : Number.NEGATIVE_INFINITY;
            const givebackRStop = highestHighSinceEntry - riskPerUnit * settings.smartTrailMaxGivebackR;

            if (Number.isFinite(atrTrailStop)) {
              nextStop = Math.max(nextStop, atrTrailStop);
            }
            if (Number.isFinite(givebackRStop)) {
              nextStop = Math.max(nextStop, givebackRStop);
            }
          }
        } else {
          const breakEvenStop = entryPrice! * (1 + settings.breakEvenOffsetPercent / 100);
          const atrTrailStop =
            atr !== null && Number.isFinite(atr)
              ? highestHighSinceEntry - atr * settings.atrMultiplier
              : Number.NEGATIVE_INFINITY;

          if (highestHighSinceEntry >= breakEvenTriggerPrice) {
            nextStop = Math.max(nextStop, breakEvenStop);
          }
          if (Number.isFinite(atrTrailStop)) {
            nextStop = Math.max(nextStop, atrTrailStop);
          }
        }

        nextStop = Math.min(nextStop, highestHighSinceEntry);
        activeStopPrice = nextStop;
      };

      let stopTouched = false;
      let stopTouchTime = candle.time;
      let stopTouchPrice: number | null = null;
      const bucketStart = bucketStartForInterval(toCandleTimeMs(candle), timeframeMs);
      const oneMinuteChildren = Number.isFinite(bucketStart)
        ? minuteChildrenByStrategyBucketStart?.get(bucketStart)
        : undefined;
      const hasFullMinuteCoverage = Boolean(
        oneMinuteChildren && oneMinuteChildren.length >= requiredMinuteChildren,
      );

      if (hasFullMinuteCoverage && oneMinuteChildren) {
        for (const oneMinuteCandle of oneMinuteChildren) {
          lowestLowSinceEntry = Math.min(lowestLowSinceEntry!, oneMinuteCandle.low);

          const stopLevel = activeStopPrice;
          if (oneMinuteCandle.low <= stopLevel) {
            stopTouched = true;
            stopTouchTime = oneMinuteCandle.time;
            stopTouchPrice = resolveLongStopExitPrice(stopLevel, oneMinuteCandle.open);
            break;
          }

          updateTrailingStop(oneMinuteCandle.high, oneMinuteCandle.low);
        }
      }

      if (!stopTouched && !hasFullMinuteCoverage) {
        updateTrailingStop(high, low);
        const stopLevel = activeStopPrice;
        if (low <= stopLevel) {
          stopTouched = true;
          stopTouchPrice = resolveLongStopExitPrice(stopLevel, candle.open);
        }
      }

      if (stopTouched) {
        const exitPrice = stopTouchPrice ?? activeStopPrice;
        const pnl = netPnlPercentFromEntryExit(
          entryPrice,
          exitPrice,
          'long',
          settings.makerFeePercent,
          settings.takerFeePercent,
        );
        const exitReason = activeStopPrice > initialStopPrice ? 'trailingStop' : 'stopLoss';
        const executionResolution = hasFullMinuteCoverage ? '1m-refined' : 'timeframe-fallback';
        const realizedR = computeRMultiple(entryPrice, exitPrice, initialStopPrice);
        const mfeR = computeRMultiple(entryPrice, highestHighSinceEntry, initialStopPrice);
        const adverseR = computeRMultiple(entryPrice, lowestLowSinceEntry, initialStopPrice);

        const trade: StrategyTrade = {
          entryIndex,
          entryPrice,
          limitPrice: entryPrice,
          entryTime: strategyCandles[entryIndex].time,
          initialStopPrice,
          finalStopPrice: activeStopPrice,
          highestHigh: highestHighSinceEntry,
          exitIndex: index,
          exitPrice,
          exitTime: stopTouchTime,
          durationCandles: index - entryIndex,
          executionResolution,
          exitReason,
          pnlPercent: pnl,
          lowestLow: lowestLowSinceEntry,
          realizedR,
          mfeR,
          maeR: adverseR === null ? null : Math.max(0, -adverseR),
        };

        trades.push(trade);
        markers.push({ index, type: 'sell', reason: exitReason });

        stats.totalTrades += 1;
        if (exitReason === 'trailingStop') {
          stats.trailingStopCount += 1;
        } else {
          stats.stopLossCount += 1;
        }
        if (pnl > 0) {
          profitableTrades += 1;
        }
        stats.totalPnlPercent += pnl;

        state = 'idle';
        pendingLimitPrice = null;
        pendingSlPrice = null;
        pendingQueuedAtMs = null;
        entryIndex = null;
        entryPrice = null;
        initialStopPrice = null;
        activeStopPrice = null;
        highestHighSinceEntry = null;
        lowestLowSinceEntry = null;
      }
    }
  }

  if (stats.totalTrades > 0) {
    stats.winRate = (profitableTrades / stats.totalTrades) * 100;
    stats.averagePnlPercent = stats.totalPnlPercent / stats.totalTrades;
  }

  const strategyStartTime = toCandleTimeMs(strategyCandles[0]);
  const strategyEndTime = toCandleTimeMs(strategyCandles[strategyCandles.length - 1]);
  if (Number.isFinite(strategyStartTime) && Number.isFinite(strategyEndTime) && strategyEndTime > strategyStartTime) {
    const days = (strategyEndTime - strategyStartTime) / (24 * 60 * 60 * 1000);
    stats.tradesPerDay = days > 0 ? stats.totalTrades / days : 0;
  }

  let activeTrade: StrategyActiveTrade | null = null;
  if (state === 'pendingBuy' && pendingLimitPrice !== null) {
    activeTrade = {
      limitPrice: pendingLimitPrice,
      stopLossPrice: pendingSlPrice!,
      state: 'pendingBuy',
      entryIndex: null,
      entryPrice: null,
    };
  } else if (
    state === 'inTrade' &&
    entryPrice !== null &&
    activeStopPrice !== null &&
    entryIndex !== null
  ) {
    activeTrade = {
      limitPrice: entryPrice,
      stopLossPrice: activeStopPrice,
      state: 'inTrade',
      entryIndex,
      entryPrice,
    };
  }

  if (indexOffset > 0) {
    for (const marker of markers) {
      marker.index += indexOffset;
    }

    for (const trade of trades) {
      trade.entryIndex += indexOffset;
      if (trade.exitIndex !== null) {
        trade.exitIndex += indexOffset;
      }
    }

    if (activeTrade && activeTrade.entryIndex !== null) {
      activeTrade = {
        ...activeTrade,
        entryIndex: activeTrade.entryIndex + indexOffset,
      };
    }
  }

  return { markers, trades, stats, activeTrade };
}
